import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Ripple } from "primereact/ripple";
import { ProgressSpinner } from "primereact/progressspinner";
import { Dialog } from "primereact/dialog";
import { Toast } from "primereact/toast";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { InputText } from "primereact/inputtext";
import NoDataCreated from "../../../../../Components/NoDataCreated";
import TextInput from "../../../../../Components/TextInput";
import ErrorPage from "../../../../../Components/ErrorPage";
import DocumentViewModal from "../../../../../Components/DocumentViewModal";
import { goToTop } from "../../../../../Components/goToTop";
import { checkIsArchitect, checkIsBuildingInspector } from "../../../../../Utils/LoginUserRoleName";
import { formatDate } from "../../../../../Utils/getDate";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";
import {
  signAndPublishPlanCheckByBI,
  signAndPublishPlanCheckByArc,
  createPlanCheckResponseByArc,
  createPlanCheckResponseByBi,
  sharePlanCheck
} from "../../../../../Api/PlanCheck/PlanCheckApiServices";
import { getAllPlanChckList } from "../../../../../Controllers/Thunks/PlanCheckThunks";
import {
  PlanCheck,
  PlanCheckPart,
  PlanCheckItemList,
} from "../../../../../Controllers/Types/PlanCheck/PlanCheckType";
import { checkEmail, checkText } from "../../../../../Utils/Validator";
import { Message } from "primereact/message";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import GoBackButton from "../../../../../Components/GoBackButton";
import ImagePreview from "../../../../../Components/ImagePreview";

const PlanCheckReport: React.FC = () => {
  const toast = useRef<Toast>(null);
  const today = new Date();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState<boolean>(false);
  const projectData = location.state?.projectData;
  const serviceId = Number(projectData.folder_id);

  const loadingPage = useAppSelector((state) => state?.planCheckList?.loader);
  const error = useAppSelector((state) => state?.planCheckList?.errorDisplay);

  const [planCheckData, setPlanCheckData] = useState<PlanCheck | null>(null);
  const [planCheckPartData, setPlanCheckPartData] = useState<PlanCheckPart[]>([]);
  const [planCheckItemData, setPlanCheckItemData] = useState<PlanCheckItemList[]>([]);
  const [showItemDocViewModal, setShowItemDocViewModal] = useState<boolean>(false);
  const [fileUrl, setFileUrl] = useState<string>("");

  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const [isSignedAndPublishedByArc, setIsSignedAndPublishedByArc] = useState<boolean>(false);
  const [isSignedAndPublishedByBi, setIsSignedAndPublishedByBi] = useState<boolean>(false);
  const [isResponseCreated, setIsResponseCreated] = useState<boolean>(false);
  const [isAllSatisfied, setIsAllSatisfied] = useState<boolean>(false);

  /* Sign by architect */
  const [showArcSignatureModal, setShowArcSignatureModal] = useState<boolean>(false);
  const [arcSignature, setArcSignature] = useState<string>("");
  const [arcSignatureError, setArcSignatureError] = useState<string>("");

  /* Sign by building inspector */
  const [showBiSignatureModal, setShowBiSignatureModal] = useState<boolean>(false);
  const [biSignature, setBiSignature] = useState<string>("");
  const [biSignatureError, setBiSignatureError] = useState<string>("");

  const [showMailModal, setShowMailModal] = useState<boolean>(false);
  const [emailAddress, setEmailAddress] = useState<string>("");
  const [emailAddressError, setEmailAddressError] = useState<string>("");


  /* PERMISION HANDLER */
  const useCanEdit = (screen: string, permission: string) => {
    return useSelector((state: RootState) =>
      hasPermission(state, screen, permission)
    );
  };

  /* Check role */
  const [isInspector, setIsInspector] = useState<boolean>(false);
  const [isArchitect, setIsArchitect] = useState<boolean>(false);

  useEffect(() => {
    const checkRole = async () => {
      const [inspectorResult, architectResult] = await Promise.all([
        checkIsBuildingInspector(),
        checkIsArchitect(),
      ]);

      setIsInspector(inspectorResult);
      setIsArchitect(architectResult);
    };

    checkRole();
  }, []);
  /*  */

  const hasPermissionAddSignature = useCanEdit("Plan Check", "add_signature");
  const havePermissionDownloadFile = useCanEdit("Plan Check", "download_file");
  /*  */

  // for fetching data fro api tigger with state object actions
  useEffect(() => {
    planCheckDataGet();
  }, [location.state]);

  const planCheckDataGet = async () => {
    await dispatch(getAllPlanChckList(serviceId));
  }

  // this is get data from state list and set projectlist
  const plancheckList = useAppSelector((state) => state.planCheckList.planCheckList);

  useEffect(() => {
    if (location.state && location.state.planCheckData) {
      const options = plancheckList && plancheckList.length > 0;
      const data = location.state.planCheckData as PlanCheck;

      if (options) {
        const collect = plancheckList.filter((ex) => ex.id === data.id);
        if (collect && collect.length > 0) {
          setPlanCheckData(collect[0]);
        }
      }
    }
  }, [location.state, plancheckList]);

  useEffect(() => {
    if (planCheckData && planCheckData !== null) {
      if (planCheckData?.is_sign_report_by_arc && planCheckData?.is_published_by_arc) {
        setIsSignedAndPublishedByArc(true);
      } else {
        setIsSignedAndPublishedByArc(false);
      }

      if (planCheckData?.is_created_responce !== null) {
        setIsResponseCreated(true);
      } else {
        setIsResponseCreated(false);
      }

      const unSatisfiedBasicItems = planCheckData?.item_list?.filter((item) => item.status === 'Open');
      const unSatisfiedDetailItemsPart = planCheckData?.plan_check_part?.filter((part) => part.item_list?.some((item) => item.status === 'Open'));

      if (
        (unSatisfiedBasicItems?.length === 0 && planCheckData?.check_type === 'Basic') ||
        (unSatisfiedDetailItemsPart?.length === 0 && planCheckData?.check_type === 'Detail')
      ) {
        setIsAllSatisfied(true);
      } else {
        setIsAllSatisfied(false);
      }

      if (planCheckData?.is_sign_report_by_bi && planCheckData?.is_published_by_bi) {
        setIsSignedAndPublishedByBi(true);
      } else {
        setIsSignedAndPublishedByBi(false);
      }
    } else {
      setIsSignedAndPublishedByArc(false);
      setIsSignedAndPublishedByBi(false);
    }
  }, [planCheckData]);

  useEffect(() => {
    if (planCheckData && planCheckData !== null) {
      if (
        planCheckData?.check_type === "Detail" &&
        planCheckData?.plan_check_part &&
        planCheckData?.plan_check_part?.length > 0
      ) {
        setPlanCheckPartData(planCheckData?.plan_check_part);
      } else {
        setPlanCheckPartData([]);
      }

      if (
        planCheckData?.check_type === "Basic" &&
        planCheckData?.item_list &&
        planCheckData?.item_list?.length > 0
      ) {
        setPlanCheckItemData(planCheckData?.item_list);
      } else {
        setPlanCheckItemData([]);
      }
    } else {
      setPlanCheckPartData([]);
      setPlanCheckItemData([]);
    }
  }, [planCheckData]);


  /* ********************************* Sign & Publish ********************************* */
  /* *********** Architect *********** */
  const handleCloseArcSignatureModal = () => {
    setShowArcSignatureModal(false);
    setArcSignature("");
    setArcSignatureError('');
  };

  const handleSignAndPublishByARC = () => {
    const planCheckType = planCheckData?.check_type;
    let haveResponsedAll: boolean = false;

    const partItemNotResponseCount = planCheckPartData.filter(part =>
      part.item_list && part.item_list.some(item => item.architect_response === null)
    ).length;

    const itemNotResponseCount = planCheckItemData?.filter(item => item?.architect_response === null)?.length;

    if (planCheckType === 'Basic') {
      if (itemNotResponseCount === 0) {
        haveResponsedAll = true;
      } else {
        haveResponsedAll = false;
      }
    } else if (planCheckType === 'Detail') {
      if (partItemNotResponseCount === 0) {
        haveResponsedAll = true;
      } else {
        haveResponsedAll = false;
      }
    }

    if (haveResponsedAll === true) {
      setShowArcSignatureModal(true);
    } else {
      confirmDialog({
        message: 'Please make sure your responses are complete before publishing.',
        header: 'Information',
        icon: 'bi bi-info-circle',
        defaultFocus: 'accept',
        acceptClassName: 'p-button-success',
        accept: () => navigate(-1),
        acceptLabel: 'Ok',
        rejectLabel: 'Close',
        resizable: false,
        draggable: false,
      });
    }
  };

  // add sign functions
  function checkArcCredentials(): boolean {
    const item = checkText(arcSignature, true, setArcSignatureError);
    return item;
  }

  useEffect(() => {
    checkText(arcSignature, false, setArcSignatureError);
  }, [arcSignature]);

  const signAndPublishByArc = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkArcCredentials() || !planCheckData) return;

    setLoading(true);

    try {
      const data = await signAndPublishPlanCheckByArc(planCheckData?.id!, 1, arcSignature);

      if (data.statusCode === 200) {
        planCheckDataGet();
        handleCloseArcSignatureModal();

        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "The plan check has been signed and published successfully.",
          life: 3000,
        });
      } else {
        toast?.current?.show({
          severity: "error",
          summary: "Failed",
          detail: "An error occurred while signing and publishing the plan check.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error signing and publishing the plan check:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while signing and publishing the plan check.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const arcSignatureModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">Sign and publish plan check</h5>

      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseArcSignatureModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const arcSignatureModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Cancel"
        className="custom_btn secondary"
        onClick={handleCloseArcSignatureModal}
      />
      <Button
        loading={loading}
        label={loading ? "Processing" : "Submit"}
        className="custom_btn primary"
        onClick={signAndPublishByArc}
      />
    </div>
  );

  /* *********** Building inspector *********** */
  const handleCloseBiSignatureModal = () => {
    setShowBiSignatureModal(false);
    setBiSignature("");
    setBiSignatureError('');
  };

  const handleSignAndPublishByBI = () => {
    const planCheckStage = planCheckData?.stage_for;
    const planCheckType = planCheckData?.check_type;
    let haveResponsedAll: boolean = false;
    let haveAddedItem: boolean = false;

    const itemsCount = planCheckItemData?.length;
    const partsCount = planCheckPartData?.length;
    const partItemsCount = planCheckPartData?.filter(part => part?.item_list?.length !== 0)?.length;

    const itemNotResponseCount = planCheckItemData?.filter(item => item?.building_inspector_response === null)?.length;

    const partItemNotResponseCount = planCheckPartData.filter(part =>
      part.item_list && part.item_list.some(item => item.building_inspector_response === null)
    ).length;

    if (planCheckType === 'Basic') {
      if (itemsCount === 0) {
        haveAddedItem = false;
      } else {
        haveAddedItem = true;
      }

      if (itemNotResponseCount === 0) {
        haveResponsedAll = true;
      } else {
        haveResponsedAll = false;
      }
    } else if (planCheckType === 'Detail') {
      if (partItemsCount === 0 || partsCount === 0) {
        haveAddedItem = false;
      } else {
        haveAddedItem = true;
      }

      if (partItemNotResponseCount === 0) {
        haveResponsedAll = true;
      } else {
        haveResponsedAll = false;
      }
    }

    if ((haveAddedItem === true && planCheckStage === 'Original') || (haveResponsedAll === true && planCheckStage === 'Response')) {
      setShowBiSignatureModal(true);
    } else {
      if (haveAddedItem === false) {
        confirmDialog({
          message: `It looks like you haven't created any items in the document. Please create at least one item to publish the document.`,
          header: 'Information',
          icon: 'bi bi-info-circle',
          defaultFocus: 'accept',
          acceptClassName: 'p-button-success',
          accept: () => navigate(-1),
          acceptLabel: 'Ok',
          rejectLabel: 'Close',
          resizable: false,
          draggable: false,
        });
      }

      if (haveResponsedAll === false) {
        confirmDialog({
          message: `Please make sure your responses are complete before publishing.`,
          header: 'Information',
          icon: 'bi bi-info-circle',
          defaultFocus: 'accept',
          acceptClassName: 'p-button-success',
          accept: () => navigate(-1),
          acceptLabel: 'Ok',
          rejectLabel: 'Close',
          resizable: false,
          draggable: false,
        });
      }
    }
  };

  // add sign database functions
  function checkBiCredentials(): boolean {
    const item = checkText(biSignature, true, setBiSignatureError);
    return item;
  }

  useEffect(() => {
    checkText(biSignature, false, setBiSignatureError);
  }, [biSignature]);


  // Sign and publish api
  const signAndPublishByBI = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkBiCredentials() || !planCheckData) return;

    setLoading(true);

    try {
      const data = await signAndPublishPlanCheckByBI(planCheckData?.id!, 1, biSignature);

      if (data.statusCode === 200) {
        planCheckDataGet();
        handleCloseBiSignatureModal();

        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "The plan check has been signed and published successfully.",
          life: 3000,
        });
      } else {
        toast?.current?.show({
          severity: "error",
          summary: "Failed",
          detail: "An error occurred while signing and publishing the plan check.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error signing and publishing the plan check:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while signing and publishing the plan check.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const biSignatureModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">Sign and publish plan check</h5>

      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseBiSignatureModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const biSignatureModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Cancel"
        className="custom_btn secondary"
        onClick={handleCloseBiSignatureModal}
      />
      <Button
        loading={loading}
        label={loading ? "Processing" : "Publish"}
        className="custom_btn primary"
        onClick={signAndPublishByBI}
      />
    </div>
  );
  /* *************************************************************************** */


  /* ********************************* ARC Response ******************************** */
  const handleCreateResponseByArc = () => {
    confirmDialog({
      message: 'Are you sure, Do you want to create a new document to respond to the plan check?',
      header: 'Confirmation',
      icon: 'bi bi-info-circle',
      defaultFocus: 'accept',
      acceptClassName: 'p-button-success',
      accept: () => createArcResponse(),
      resizable: false,
      draggable: false,
    });
  }

  const createArcResponse = async () => {
    if (planCheckData === null) return;

    try {
      const response = await createPlanCheckResponseByArc(planCheckData?.id);

      if (response.statusCode === 200) {
        await planCheckDataGet();

        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "The response was successfully created.",
          life: 3000,
        });

        const navigateToList = () => {
          navigate(
            '/project/detail/default-service/pre-commencement/plan-check/list',
            { state: { data: projectData } }
          );
        };

        if (response?.data && plancheckList?.length > 0) {
          const newPlanCheck = response?.data;
          const planCheckPath = planCheckData?.check_type?.toLowerCase();

          if (newPlanCheck) {
            setTimeout(() => {
              navigate(`/project/detail/default-service/pre-commencement/plan-check/${planCheckPath}`, {
                replace: true,
                state: {
                  data: newPlanCheck,
                  projectData: projectData,
                  fromArcResponse: true,
                },
              });
            }, 500);
          } else {
            navigateToList();
          }
        } else {
          navigateToList();
        }
      } else {
        toast?.current?.show({
          severity: "error",
          summary: "Failed",
          detail: "An error occurred while creating the response.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error creating the response:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while creating the response.",
        life: 3000,
      });
    }
  }
  /* *************************************************************************** */


  /* ********************** Response create by bi *********************** */
  const handleCreateResponseByBi = () => {
    confirmDialog({
      message: 'Are you sure, Do you want to create a new document to respond to the plan check?',
      header: 'Confirm the Publishion',
      icon: 'bi bi-info-circle',
      defaultFocus: 'accept',
      acceptClassName: 'p-button-danger',
      accept: () => createBiResponse(),
      resizable: false,
      draggable: false,
      dismissableMask: true
    });
  }

  const createBiResponse = async () => {
    if (planCheckData === null) return;

    try {
      const response = await createPlanCheckResponseByBi(planCheckData?.id!);

      if (response.statusCode === 200) {
        planCheckDataGet();

        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "The response was successfully created.",
          life: 3000,
        });

        const navigateToList = () => {
          navigate(
            '/project/detail/default-service/pre-commencement/plan-check/list',
            { state: { data: projectData } }
          );
        };

        if (response?.data && plancheckList?.length > 0) {
          const newPlanCheck = response?.data;
          const planCheckPath = planCheckData?.check_type?.toLowerCase();

          if (newPlanCheck) {
            setTimeout(() => {
              navigate(`/project/detail/default-service/pre-commencement/plan-check/${planCheckPath}`, {
                replace: true,
                state: {
                  data: newPlanCheck,
                  projectData: projectData,
                  fromArcResponse: true,
                },
              });
            }, 500);
          } else {
            navigateToList();
          }
        } else {
          navigateToList();
        }
      } else {
        toast?.current?.show({
          severity: "error",
          summary: "Failed",
          detail: "An error occurred while creating the response.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error creating the response:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while creating the response.",
        life: 3000,
      });
    }
  }
  /* *************************************************************************** */


  /* *********************************** Mail ********************************** */
  const handleCloseMailModal = () => {
    setShowMailModal(false);
    setEmailAddress("");
  };

  const handleAddMail = () => {
    setShowMailModal(true);
  };

  // need to add email for send report.
  // validations.
  function checkCredentialsEmail(): boolean {
    const mail = checkEmail(emailAddress, true, setEmailAddressError);
    return mail;
  }

  // clear message
  useEffect(() => {
    checkEmail(emailAddress, false, setEmailAddressError);
  }, [emailAddress]);

  // database error set message.
  function showAPIErrorEmail(keyName: string, error: string): void {
    if (keyName === "email") {
      setEmailAddressError(error);
    }
  }

  // send mail database functions.
  const handleSendMail = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!checkCredentialsEmail() || !planCheckData) return;

    setLoading(true);

    try {
      const data = await sharePlanCheck(planCheckData?.id!, emailAddress);

      if (data.statusCode === 200) {
        planCheckDataGet();
        handleCloseMailModal();

        toast?.current?.show({
          severity: "success",
          summary: "Success",
          detail: "Email sent successfully.",
          life: 3000,
        });
      } else if (data.statusCode === 422) {
        // database error
        Object.entries(data.errors ?? []).map(([key, value], index) =>
          showAPIErrorEmail(key, value[0])
        );
      } else {
        toast?.current?.show({
          severity: "error",
          summary: "Failed",
          detail: "There was an error sending the email.",
          life: 3000,
        });
      }
    } catch (error) {
      console.error("Error sending the email:", error);
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: "Something went wrong while sending the email.",
        life: 3000,
      });
    } finally {
      setLoading(false);
    }
  };

  const mailModalHeader = (
    <div className="custom_modal_header_inner">
      <h5 className="modal-title fs-5">Send report via mail</h5>
      <button
        type="button"
        className="close_modal_btn p-ripple"
        onClick={handleCloseMailModal}
      >
        <i className="bi bi-x-circle"></i>
        <Ripple />
      </button>
    </div>
  );

  const mailModalFooter = (
    <div className="custom_modal_footer">
      <Button
        label="Cancel"
        className="custom_btn secondary"
        onClick={handleCloseMailModal}
      />
      <Button
        loading={loading}
        label={`${loading ? "Sending" : "Send"}`}
        className="custom_btn primary"
        onClick={handleSendMail}
      />
    </div>
  );

  const handleViewItemFile = (fileUrl: string) => {
    if (fileUrl && fileUrl !== "") {
      setShowItemDocViewModal(true);
      setFileUrl(fileUrl);
    }
  };

  const handleCloseFileViewModal = () => {
    setShowItemDocViewModal(false);
  };
  /* *************************************************************************** */

  /* ******************************** Download ********************************* */
  const handleDownloadReport = async () => {
    if (isDownloading) return;
    // const reportPath = planCheckData?.file_download_path;
    const reportPath = planCheckData?.file_download_path;
    if (!reportPath) {
      if (toast.current) {
        toast.current.show({
          severity: "error",
          summary: "Failed",
          detail: "File not found!",
          life: 3000,
        });
      }
      return;
    }

    setIsDownloading(true);

    setTimeout(async () => {
      try {
        const response = await fetch(reportPath);

        if (!response.ok) {
          throw new Error("Failed to fetch the file.");
        }

        const blob = await response.blob();
        const blobUrl = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = blobUrl;
        link.download = reportPath.split("/").pop() || "plan check report.pdf";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(blobUrl);

        if (toast.current) {
          toast.current.show({
            severity: "success",
            summary: "Success",
            detail: "File downloaded successfully.",
            life: 3000,
          });
        }
      } catch (error) {
        if (toast.current) {
          toast.current.show({
            severity: "error",
            summary: "Failed",
            detail: "Error downloading the file",
            life: 3000,
          });
        }
      } finally {
        setIsDownloading(false);
      }
    }, 500);
  };

  const handleViewReportDocument = () => {
    if (planCheckData?.file_download_path) {
      setShowItemDocViewModal(true);
      setFileUrl(planCheckData?.file_download_path);
    }
  };
  /* *************************************************************************** */

  /* **************************** Common functions ***************************** */
  const exitReportPreview = () => {
    navigate(
      '/project/detail/default-service/pre-commencement/plan-check/list',
      { state: { data: projectData } }
    );

    goToTop();
  }
  /* *************************************************************************** */

  return (
    <React.Fragment>
      <Toast ref={toast} />

      <div className="main_content full_screen_view">
        {loadingPage ? (
          <div className="loading_container">
            <ProgressSpinner
              className="progresscircle custom"
              style={{ width: "50px", height: "50px", stroke: "#8953D7" }}
              strokeWidth="8"
              fill="var(--surface-ground)"
              animationDuration="1s"
            />
          </div>
        ) : error ? (
          <ErrorPage errorMessage={error} />
        ) : (
          <div className="page_content mt-0">
            <div className="page_breadcrumb_area">
              <ul className="page_breadcrumb">
                {isArchitect && (
                  <li className="page_breadcrumb_item has_child has_link"
                    onClick={() => navigate('/project')}
                  >
                    Shared folders
                  </li>
                )}

                <li
                  className={`page_breadcrumb_item has_child ${isInspector ? 'has_link' : ''}`}
                  onClick={() => isInspector ? navigate('/project/detail', { state: { projectData: projectData } }) : null}
                >
                  {projectData.job_number} : {projectData.address}
                </li>

                <li className="page_breadcrumb_item has_child">
                  {projectData?.folder}
                </li>

                <li
                  className="page_breadcrumb_item has_child has_link"
                  onClick={() => navigate(-2)}
                >
                  {projectData?.feature_name}
                </li>

                <li
                  className="page_breadcrumb_item has_child has_link"
                  onClick={() => navigate(-1)}
                >
                  {projectData?.feature_name} list
                </li>

                <li className="page_breadcrumb_item has_child">
                  {projectData?.feature_name} - {planCheckData?.check_type}
                </li>

                <li className="page_breadcrumb_item active">
                  {projectData?.feature_name} Report
                </li>
              </ul>

              {(isInspector && isSignedAndPublishedByBi) || (isArchitect && isSignedAndPublishedByArc) ? (
                <button className="back_btn p-ripple" onClick={exitReportPreview}>
                  <i className="bi bi-x-lg me-2"></i>
                  Close
                  <Ripple />
                </button>
              ) : (
                <GoBackButton />
              )}
            </div>

            <div className="page_header_area justify-content-end mt-sm-3 mt-2">
              <div className="page_action_area">
                {isInspector && (
                  <React.Fragment>
                    {(!isResponseCreated && planCheckData?.stage_for === 'Response') && (
                      <button
                        className="add_data_link_btn p-ripple"
                        onClick={handleCreateResponseByBi}
                      >
                        <i className="bi bi-reply"></i>
                        <span>Response</span>
                        <Ripple />
                      </button>
                    )}

                    {hasPermissionAddSignature && (
                      (isSignedAndPublishedByBi === false && !isResponseCreated && planCheckData?.stage_for === 'Original') && (
                        <button
                          className="add_data_link_btn p-ripple"
                          onClick={handleSignAndPublishByBI}
                        >
                          <i className="bi bi-send"></i>
                          <span>Sign & Publish</span>
                          <Ripple />
                        </button>
                      )
                    )}
                  </React.Fragment>
                )}

                {isArchitect && (
                  <React.Fragment>
                    {(!isResponseCreated && planCheckData?.stage_for === 'Original' && !isAllSatisfied) && (
                      <button
                        className="add_data_link_btn p-ripple"
                        onClick={handleCreateResponseByArc}
                      >
                        <i className="bi bi-reply"></i>
                        <span>Response</span>
                        <Ripple />
                      </button>
                    )}

                    {hasPermissionAddSignature && (
                      (isSignedAndPublishedByArc === false && !isResponseCreated && planCheckData?.stage_for === 'Response') && (
                        <React.Fragment>
                          <button
                            className="add_data_link_btn p-ripple"
                            onClick={handleSignAndPublishByARC}
                          >
                            <i className="bi bi-send"></i>
                            <span>Sign & Publish</span>
                            <Ripple />
                          </button>
                        </React.Fragment>
                      )
                    )}
                  </React.Fragment>
                )}

                {planCheckData?.file_download_path !== null && (
                  <React.Fragment>
                    {havePermissionDownloadFile && (
                      <button
                        className="add_data_link_btn p-ripple"
                        onClick={handleDownloadReport}
                        disabled={isDownloading}
                      >
                        {isDownloading ? (
                          <ProgressSpinner
                            style={{ width: "20px", height: "20px" }}
                            strokeWidth="5"
                            fill="none"
                            animationDuration="1s"
                            className="spin_loader"
                          />
                        ) : (
                          <i className="bi bi-download"></i>
                        )}
                        <span>{isDownloading ? "Downloading" : "Download"}</span>
                        <Ripple />
                      </button>
                    )}

                    {isInspector && (
                      <button
                        className="add_data_link_btn p-ripple"
                        onClick={handleAddMail}
                      >
                        <i className="bi bi-envelope"></i>
                        <span>Send</span>
                        <Ripple />
                      </button>
                    )}
                  </React.Fragment>
                )}
              </div>
            </div>

            {planCheckData && planCheckData !== null ? (
              <article className="site_visit_report_container">
                <div className="sv_report_header">
                  <div className="sv_report_header_sub">
                    <div className="sv_report_header_content">
                      <h5>Project:&nbsp;
                        <span>{projectData ? projectData?.job_number + ": " + projectData?.address : "----"} </span>
                      </h5>
                    </div>
                  </div>
                  <div className="sv_report_header_sub">
                    <div className="sv_report_header_content">
                      <h5>Description:&nbsp;
                        <span>{projectData && projectData?.description ? projectData?.description : "----"}</span>
                      </h5>
                    </div>
                  </div>
                  <div className="sv_report_header_sub">
                    <div className="sv_report_header_content">
                      <h5>Date:&nbsp;
                        <span>{formatDate(today)}</span>
                      </h5>
                    </div>
                    <div className="sv_report_header_content">
                      <h5>Inspector name:&nbsp;
                        <span>{planCheckData.inspector_name}</span>
                      </h5>
                    </div>
                  </div>
                  <div className="sv_report_header_sub mb-0">
                    <div className="sv_report_header_content">
                      <h5>Stage:&nbsp;
                        <span>{planCheckData?.stage}</span>
                      </h5>
                    </div>
                  </div>
                </div>

                <div className="sv_report_body">
                  {/* <div className="checklist_status_header">
                    <h5 className="text_warning">Open: {String(0).padStart(2, '0')}</h5>
                    <h5 className="text_success">Closed: {String(0).padStart(2, '0')}</h5>
                  </div> */}

                  <div className="status_lable_container">
                    <div className="status_lable_area p-0 mb-3">
                      <div className="status_lable">
                        <span style={{ backgroundColor: "#FFEFBC" }}></span>
                        <h6>Open</h6>
                      </div>
                      <div className="status_lable">
                        <span style={{ backgroundColor: "#B9F6CA" }}></span>
                        <h6>Closed</h6>
                      </div>
                    </div>
                  </div>

                  {/* For Detail plan check */}
                  {planCheckData?.check_type === "Detail" ? (
                    <React.Fragment>
                      {planCheckPartData && planCheckPartData?.length > 0 ? (
                        <div className="sv_report_table_area table-responsive pl_table_area">
                          <table className="table table-bordered sv_report_table pl_table mb-0">
                            <thead>
                              <tr className="sv_report_table_header light">
                                <th style={{ width: "5%" }} className="item_code_column">
                                  Item code
                                </th>

                                <th style={{ width: "80%" }} className="comment_column">
                                  Comment
                                </th>

                                <th style={{ width: "15%" }} className="files_column">
                                  Files
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {planCheckPartData?.map((part) => (
                                <React.Fragment>
                                  <tr key={part?.id} className="sv_report_table_data data_group_row">
                                    <td colSpan={4}>
                                      {part?.part_number + " - " + part?.part_title}
                                    </td>
                                  </tr>

                                  {part?.item_list && part?.item_list?.length > 0 && (
                                    <React.Fragment>
                                      {part?.item_list?.map((item) => (
                                        <tr
                                          key={item?.id}
                                          className="sv_report_table_data inner_tabel_row"
                                          style={{ backgroundColor: item?.status === "Open" ? "#FFEFBC" : "#B9F6CA" }}
                                        >
                                          <td style={{ width: "5%" }} className="item_code_column">
                                            {item?.item_code}
                                          </td>

                                          <td style={{ width: "80%" }} className="comment_column first_letter_capitalized">
                                            {item?.comment}
                                          </td>

                                          <td style={{ width: "15%" }} className="files_column">
                                            <div className="item_file_area mt-1">
                                              {item && item?.file_list && item?.file_list?.length > 0 ? (
                                                <React.Fragment>
                                                  {item?.file_list?.map((file, index) => {
                                                    return (
                                                      <React.Fragment>
                                                        {file?.extension === "pdf" ? (
                                                          <div className="item_file_label" key={`detail_item_pdf_${index}`}>
                                                            <div
                                                              className="item_doc p-ripple"
                                                              onClick={() => handleViewItemFile(file?.path!)}
                                                            >
                                                              <i className="bi bi-file-earmark-pdf"></i>
                                                            </div>
                                                          </div>
                                                        ) : (
                                                          <div className="item_file_label" key={`detail_item_image_${index}`}>
                                                            <ImagePreview
                                                              src={file?.path!}
                                                              alt={`detail_item_file_${index}`}
                                                              className="small"
                                                            />
                                                          </div>
                                                        )}
                                                      </React.Fragment>
                                                    );
                                                  }
                                                  )}
                                                </React.Fragment>
                                              ) : (
                                                <div className="no_file_found">
                                                  ----
                                                </div>
                                              )}
                                            </div>
                                          </td>
                                        </tr>
                                      ))}
                                    </React.Fragment>
                                  )}
                                </React.Fragment>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="no_report_data">
                          <h6>
                            <i className="bi bi-exclamation-circle me-1"></i>
                            No data found!
                          </h6>
                        </div>
                      )}
                    </React.Fragment>
                  ) : planCheckData?.check_type === "Basic" ? (
                    <React.Fragment>
                      {planCheckItemData && planCheckItemData?.length > 0 ? (
                        <div className="sv_report_table_area table-responsive pl_table_area">
                          <table className="table table-bordered sv_report_table pl_table mb-0">
                            <thead>
                              <tr className="sv_report_table_header light">
                                <th style={{ width: "5%" }} className="item_code_column">
                                  Item code
                                </th>

                                <th style={{ width: "80%" }} className="comment_column">
                                  Comment
                                </th>

                                <th style={{ width: "15%" }} className="files_column">
                                  Files
                                </th>
                              </tr>
                            </thead>

                            <tbody>
                              {planCheckItemData?.map((item) => (
                                <tr
                                  key={item?.id}
                                  className="sv_report_table_data inner_tabel_row"
                                  style={{ backgroundColor: item?.status === "Open" ? "#FFEFBC" : "#B9F6CA" }}
                                >
                                  <td style={{ width: "5%" }} className="item_code_column">
                                    {item?.item_code}
                                  </td>

                                  <td style={{ width: "80%" }} className="comment_column first_letter_capitalized">
                                    {item?.comment}
                                  </td>

                                  <td style={{ width: "15%" }} className="files_column">
                                    <div className="item_file_area mt-1">
                                      {item && item?.file_list && item?.file_list?.length > 0 ? (
                                        <React.Fragment>
                                          {item?.file_list?.map((file, index) => {
                                            return (
                                              <React.Fragment>
                                                {file?.extension === "pdf" ? (
                                                  <div className="item_file_label" key={`basic_item_pdf_${index}`}>
                                                    <div
                                                      className="item_doc p-ripple"
                                                      onClick={() => handleViewItemFile(file?.path!)}
                                                    >
                                                      <i className="bi bi-file-earmark-pdf"></i>
                                                    </div>
                                                  </div>
                                                ) : (
                                                  <div className="item_file_label" key={`basic_item_image_${index}`}>
                                                    <ImagePreview
                                                      src={file?.path!}
                                                      alt={`basic_item_file_${index}`}
                                                      className="small"
                                                    />
                                                  </div>
                                                )}
                                              </React.Fragment>
                                            );
                                          })}
                                        </React.Fragment>
                                      ) : (
                                        <div className="no_file_found">
                                          ----
                                        </div>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      ) : (
                        <div className="no_report_data">
                          <h6>
                            <i className="bi bi-exclamation-circle me-1"></i>
                            No data found!
                          </h6>
                        </div>
                      )}
                    </React.Fragment>
                  ) : null}

                  {(isSignedAndPublishedByBi === true || isSignedAndPublishedByArc === true) && (
                    <div className="sv_report_footer">
                      <div className="sv_report_footer_item text-start">
                        {isSignedAndPublishedByBi === true && planCheckData?.stage_for === 'Original' && (
                          <React.Fragment>
                            <h4>Building Inspector</h4>
                            <h3>{planCheckData?.sign_by_bi}</h3>
                            {/* <h5>Prepared by</h5> */}
                            <h6>{planCheckData?.sign_date_by_bi}</h6>
                          </React.Fragment>
                        )}
                      </div>

                      <div className="sv_report_footer_item">
                        {isSignedAndPublishedByArc === true && planCheckData?.stage_for === 'Response' && (
                          <React.Fragment>
                            <h4>Architect</h4>
                            <h3>{planCheckData?.sign_by_arc}</h3>
                            {/* <h5>Prepared by</h5> */}
                            <h6>{planCheckData?.sign_date_by_arc}</h6>
                          </React.Fragment>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </article>
            ) : (
              <div className="page_main_content_section no_header">
                <NoDataCreated message={`No data found!`} />
              </div>
            )}
          </div>
        )}
      </div>


      {/* Mail modal */}
      <Dialog
        header={mailModalHeader}
        footer={mailModalFooter}
        headerClassName="custom_modal_header"
        visible={showMailModal}
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseMailModal}
        dismissableMask
      >
        <div className="custom_modal_body">
          <div className="p-1 p-sm-3 pb-sm-0 w-100">
            <div className="row">
              <div className="col-12">
                <TextInput
                  id="emailAddress"
                  label="E-mail address"
                  labelHtmlFor="emailAddress"
                  required={true}
                  value={emailAddress}
                  placeholder="abcd@gmail.com"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setEmailAddress(e.target.value)
                  }
                  error={emailAddressError}
                  keyFilter={"email"}
                  formGroupClassName="mb-2"
                />

                {planCheckData?.file_download_path !== null && (
                  <div
                    className="file_attached_area"
                    onClick={handleViewReportDocument}
                  >
                    <i className="bi bi-paperclip"></i>
                    <h6>Plan check report.pdf</h6>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}


      {/* Architect sign and publish modal */}
      <Dialog
        visible={showArcSignatureModal}
        header={arcSignatureModalHeader}
        footer={arcSignatureModalFooter}
        headerClassName="custom_modal_header"
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseArcSignatureModal}
        dismissableMask
      >
        <div className="custom_modal_body">
          <div className="p-1 p-sm-3 pb-sm-0 w-100">
            <div className="row">
              <div className="col-12">
                <div className="page_form_group">
                  <Message
                    severity='info'
                    className="message_text"
                    icon="bi bi-info-circle"
                    style={{ border: '1px solid', borderRadius: '8px' }}
                    text={`Once you sign and publish this report, it will automatically be shared with the building inspector, who will be able to view and respond to it.`}
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="page_form_group">
                  <label htmlFor="arcSignature" className="custom_form_label is_required">
                    Signature
                  </label>

                  <InputText
                    id="arcSignature"
                    value={arcSignature}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setArcSignature(e.target.value)}
                    className="custom_form_input signature_input"
                    placeholder="Enter your signature"
                    autoFocus
                    type="text"
                  />

                  {arcSignatureError && (
                    <small className="form_error_msg">{arcSignatureError}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}


      {/* Building inspector sign and publish modal */}
      <Dialog
        visible={showBiSignatureModal}
        header={biSignatureModalHeader}
        footer={biSignatureModalFooter}
        headerClassName="custom_modal_header"
        className="custom_modal_dialog modal_dialog_sm"
        onHide={handleCloseBiSignatureModal}
        dismissableMask
      >
        <div className="custom_modal_body">
          <div className="p-1 p-sm-3 pb-sm-0 w-100">
            <div className="row">
              <div className="col-12">
                <div className="page_form_group">
                  <Message
                    severity='info'
                    className="message_text"
                    icon="bi bi-info-circle"
                    style={{ border: '1px solid', borderRadius: '8px' }}
                    text={`Once you sign and publish this report, it will automatically be shared with the architect, who will be able to view and respond to it.`}
                  />
                </div>
              </div>

              <div className="col-12">
                <div className="page_form_group">
                  <label
                    htmlFor="biSignature"
                    className="custom_form_label is_required"
                  >
                    Signature
                  </label>

                  <InputText
                    id="biSignature"
                    value={biSignature}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setBiSignature(e.target.value)}
                    className="custom_form_input signature_input"
                    placeholder="Enter your signature"
                    autoFocus
                    type="text"
                  />

                  {biSignatureError && (
                    <small className="form_error_msg">{biSignatureError}</small>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Dialog>
      {/*  */}


      {/* Document view modal */}
      <DocumentViewModal
        isOpen={showItemDocViewModal}
        onClose={handleCloseFileViewModal}
        fileUrl={fileUrl}
      />
      {/*  */}
    </React.Fragment>
  );
};

export default PlanCheckReport;
